import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { FormikField } from "@air/formik-field";
import { Button } from "@air/primitive-button";
import { Input } from "@air/primitive-input";
import { Modal, ModalCloseButton, ModalTitle } from "@air/primitive-modal";
import { Textarea } from "@air/primitive-textarea";
import { Formik, useField } from "formik";
import { memo } from "react";
import * as Yup from "yup";
import { BoardId } from "~/components/EditPrivateBoardModal/EditBoardModal/BoardId/BoardId";
import Form from "~/components/Form";
import { EDIT_BOARD_INPUT } from "~/constants/testIDs";
import { useShowSubscriptionExpiredModal } from "~/hooks/useShowSubscriptionExpiredModal";
import { useUpdateBoard } from "~/swr-hooks/boards/useUpdateBoard";
import { useBoardSlackIntegrations } from "~/swr-hooks/slack/useBoardSlackIntegrations";
import { convertUnknownToError } from "~/utils/ErrorUtils";
import { containsRestrictedPathChars } from "~/utils/FileUtils";
import { SlackIntegration } from "./SlackIntegration/SlackIntegration";
const EditBoardSchema = Yup.object({
    title: Yup.string().trim().max(255, "Cannot be longer than 255 characters").test("restricted-char-validation", "Characters : and | cannot be used", (val)=>!containsRestrictedPathChars(val || "")).required("This field is required"),
    description: Yup.string()
});
const DescriptionTextArea = /*#__PURE__*/ memo(()=>{
    const [field] = useField("description");
    return /*#__PURE__*/ _jsx(Textarea, {
        size: "large",
        ...field
    });
});
DescriptionTextArea.displayName = "DescriptionTextArea";
export const EditBoardModal = (param)=>{
    let { board, onClose, trackLocation } = param;
    const { description, title } = board;
    const { data: slackIntegrationData } = useBoardSlackIntegrations(board.id);
    const { showingSubscriptionExpiredModal } = useShowSubscriptionExpiredModal({
        onClose
    });
    const { updateBoard } = useUpdateBoard();
    if (showingSubscriptionExpiredModal) {
        return null;
    }
    return /*#__PURE__*/ _jsx(Formik, {
        validationSchema: EditBoardSchema,
        onSubmit: async (param, param1)=>{
            let { title, description } = param, { setErrors } = param1;
            return updateBoard.mutate({
                board,
                values: {
                    title,
                    description
                },
                trackLocation
            }, {
                onError: (error)=>{
                    const _error = convertUnknownToError(error);
                    setErrors({
                        title: _error.message
                    });
                },
                onSuccess: ()=>{
                    onClose();
                }
            });
        },
        initialValues: {
            title,
            description: description || ""
        },
        render: (FormikProps)=>/*#__PURE__*/ _jsxs(Modal, {
                className: "flex flex-col gap-4",
                dangerouslyBypassFocusLock: true,
                dangerouslyBypassScrollLock: true,
                "data-testid": "EDIT_BOARD_DETAILS_MODAL",
                isOpen: true,
                onDismiss: onClose,
                children: [
                    /*#__PURE__*/ _jsxs("header", {
                        className: "flex justify-between gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(ModalTitle, {
                                children: "Edit details"
                            }),
                            /*#__PURE__*/ _jsx(ModalCloseButton, {
                                onClick: onClose
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(Form, {
                        className: "flex flex-col gap-6",
                        id: "edit-board",
                        "data-testid": EDIT_BOARD_INPUT,
                        children: [
                            /*#__PURE__*/ _jsx(FormikField, {
                                component: /*#__PURE__*/ _jsx(Input, {
                                    autoFocus: true,
                                    size: "large"
                                }),
                                id: "title",
                                label: "Board name",
                                name: "title"
                            }),
                            /*#__PURE__*/ _jsx(FormikField, {
                                component: /*#__PURE__*/ _jsx(Textarea, {}),
                                id: "description",
                                label: "Description (Optional)",
                                name: "description"
                            })
                        ]
                    }),
                    !!slackIntegrationData && /*#__PURE__*/ _jsx(SlackIntegration, {
                        integration: slackIntegrationData[0],
                        board: board
                    }),
                    !!board.id && /*#__PURE__*/ _jsx(BoardId, {
                        id: board.id
                    }),
                    /*#__PURE__*/ _jsxs("footer", {
                        className: "flex items-center justify-end gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(Button, {
                                appearance: "ghost",
                                color: "grey",
                                onClick: onClose,
                                size: "large",
                                children: "Cancel"
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                appearance: "filled",
                                color: "blue",
                                "data-testid": "EDIT_BOARD_MODAL_SAVE_BUTTON",
                                disabled: !FormikProps.isValid,
                                form: "edit-board",
                                isLoading: updateBoard.isPending,
                                size: "large",
                                type: "submit",
                                children: "Save"
                            })
                        ]
                    })
                ]
            })
    });
};
